import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2,
  PauseCircle,
  Copy,
  Pencil,
  Trash2,
  Plus,
  MessageCircle,
  ChevronUp,
  ChevronDown,
  LayoutTemplate,
  Wand2,
  ImageIcon,
  Mic,
  FileText,
  Bold,
  Italic,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Quote,
} from "lucide-react";
import {
  TIMING_OPTIONS,
  VARIABLE_BUTTONS,
  READY_MESSAGES,
  CONTENT_TYPE_OPTIONS,
  substituteVariables,
  contentTypeLabel,
  timingPhrase,
  findTimingSelectValue,
  stepOrdinalLabel,
  stepToApiPayload,
} from "@/pages/recovery/recoveryHelpers";
import {
  wrapTextareaSelection,
  prefixLinesInTextarea,
  insertListLine,
  StepContentPreview,
  WhatsAppAudioBubble,
} from "@/pages/recovery/whatsappPreview";
import { RecoveryFileDropzone } from "@/pages/recovery/RecoveryFileDropzone";
import { RecoveryImageUploadHint } from "@/pages/recovery/RecoveryImageUploadHint";

function emptyModelForm() {
  return {
    id: null,
    name: "",
    content_type: "text",
    text_content: "",
    media_url: "",
    caption: "",
    mimetype: "",
    filename: "",
    audio_title: "",
  };
}

export default function RecoveryPage() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, selected_model: "leve" });
  const [presets, setPresets] = useState([]);
  const [steps, setSteps] = useState([]);
  const [models, setModels] = useState([]);

  const [modelEditorOpen, setModelEditorOpen] = useState(false);
  const [modelForm, setModelForm] = useState(emptyModelForm());

  const [stepEditorOpen, setStepEditorOpen] = useState(false);
  const [stepDraft, setStepDraft] = useState(null);
  /** "custom" timing: number + unit */
  const [stepCustomTiming, setStepCustomTiming] = useState(false);
  const [stepCustomNum, setStepCustomNum] = useState(10);
  const [stepCustomUnit, setStepCustomUnit] = useState("m");
  const [modelsSectionOpen, setModelsSectionOpen] = useState(false);
  const stepTextareaRef = useRef(null);
  const modelTextareaRef = useRef(null);

  const stepTextFieldValue = stepDraft?.content_type === "image_caption" ? stepDraft.caption ?? "" : stepDraft?.text_content ?? "";

  const setStepTextFieldValue = useCallback((next) => {
    setStepDraft((p) => {
      if (!p) return p;
      return p.content_type === "image_caption" ? { ...p, caption: next } : { ...p, text_content: next };
    });
  }, []);

  const wrapStepFormat = (before, after) => {
    const ta = stepTextareaRef.current;
    wrapTextareaSelection(ta, stepTextFieldValue, setStepTextFieldValue, before, after);
  };

  const modelTextFieldValue =
    modelForm.content_type === "image_caption" ? modelForm.caption ?? "" : modelForm.text_content ?? "";

  const setModelTextFieldValue = useCallback((next) => {
    setModelForm((p) => (p.content_type === "image_caption" ? { ...p, caption: next } : { ...p, text_content: next }));
  }, []);

  const wrapModelFormat = (before, after) => {
    wrapTextareaSelection(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, before, after);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: st }, { data: t }, { data: m }] = await Promise.all([
        api.get("/recovery/settings"),
        api.get("/recovery/steps"),
        api.get("/recovery/templates").catch(() => ({ data: { templates: [] } })),
        api.get("/recovery/message-models").catch(() => ({ data: { models: [] } })),
      ]);
      setSettings({
        enabled: Boolean(s?.enabled),
        selected_model: s?.selected_model || "leve",
      });
      setSteps(Array.isArray(st?.steps) ? st.steps : []);
      setPresets(Array.isArray(t?.templates) ? t.templates : []);
      setModels(Array.isArray(m?.models) ? m.models : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível carregar a recuperação de PIX");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
    [steps],
  );

  const statusHint = "Quando ativo, o ConvertPix envia lembretes somente para PIX pendentes.";

  const openNewModel = () => {
    setModelForm(emptyModelForm());
    setModelEditorOpen(true);
  };

  const openEditModel = (row) => {
    setModelForm({
      id: row.id,
      name: row.name || "",
      content_type: row.content_type || "text",
      text_content: row.text_content || "",
      media_url: row.media_url || "",
      caption: row.caption || "",
      mimetype: row.mimetype || "",
      filename: row.filename || "",
      audio_title: row.audio_title || "",
    });
    setModelEditorOpen(true);
  };

  const onPickModelFile = async (file) => {
    if (!file) return;
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ""));
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
    setModelForm((prev) => ({
      ...prev,
      media_url: dataUrl,
      mimetype: file.type || prev.mimetype || "",
      filename: file.name || prev.filename || "",
    }));
  };

  const insertVarIntoModel = (token, field) => {
    setModelForm((prev) => ({
      ...prev,
      [field]: `${prev[field] || ""}${token}`,
    }));
  };

  const saveModel = async () => {
    const name = String(modelForm.name || "").trim();
    if (!name) {
      toast.error("Dê um nome ao modelo");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        content_type: modelForm.content_type || "text",
        text_content: modelForm.text_content || "",
        media_url: modelForm.media_url || null,
        caption: modelForm.caption || "",
        mimetype: modelForm.mimetype || null,
        filename: modelForm.filename || null,
        audio_title: modelForm.audio_title?.trim() || null,
      };
      if (modelForm.id) {
        await api.put(`/recovery/message-models/${modelForm.id}`, payload);
        toast.success("Modelo atualizado");
      } else {
        await api.post("/recovery/message-models", payload);
        toast.success("Modelo criado");
      }
      setModelEditorOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar o modelo");
    } finally {
      setSaving(false);
    }
  };

  const deleteModel = async (row) => {
    if (!window.confirm(`Excluir o modelo "${row.name}"?`)) return;
    try {
      await api.delete(`/recovery/message-models/${row.id}`);
      toast.success("Modelo removido");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível excluir");
    }
  };

  const duplicateModel = async (row) => {
    try {
      await api.post(`/recovery/message-models/${row.id}/duplicate`);
      toast.success("Modelo duplicado");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao duplicar");
    }
  };

  const openNewStep = () => {
    if (!models.length) {
      toast.message("Crie pelo menos um modelo de mensagem", {
        description: "Cada lembrete usa um modelo (texto, imagem ou áudio). Use “Novo modelo” ao lado.",
      });
      return;
    }
    const nextOrder = sortedSteps.length ? Math.max(...sortedSteps.map((s) => Number(s.order || 0))) + 1 : 0;
    const first = TIMING_OPTIONS[0];
    setStepDraft({
      id: null,
      order: nextOrder,
      delay_value: first.delay_value,
      delay_unit: first.delay_unit,
      step_label: null,
      message_model_id: models[0].id,
      content_type: "text",
      text_content: "",
      media_url: null,
      caption: "",
      active: true,
      mimetype: null,
      filename: null,
    });
    setStepCustomTiming(false);
    setStepCustomNum(10);
    setStepCustomUnit("m");
    setStepEditorOpen(true);
  };

  const openStepEditor = (step) => {
    const dv = Number(step.delay_value ?? 10);
    const du = step.delay_unit || "m";
    const hit = TIMING_OPTIONS.find((o) => o.delay_value === dv && o.delay_unit === du);
    setStepCustomTiming(!hit);
    setStepCustomNum(dv);
    setStepCustomUnit(du || "m");
    setStepDraft({
      ...step,
      step_label: step.step_label ?? null,
    });
    setStepEditorOpen(true);
  };

  const saveStep = async () => {
    if (!stepDraft) return;
    const delay_value = stepCustomTiming ? Number(stepCustomNum || 1) : Number(stepDraft.delay_value ?? 10);
    const delay_unit = stepCustomTiming ? stepCustomUnit : stepDraft.delay_unit || "m";
    const draft = { ...stepDraft, delay_value, delay_unit };
    const mid = String(draft.message_model_id || "").trim();
    const useModel = Boolean(mid);

    const payload = useModel ?
        {
          ...stepToApiPayload({
            ...draft,
            message_model_id: mid,
            step_label: draft.step_label?.trim() || null,
          }),
          text_content: "",
          media_url: null,
          caption: "",
          mimetype: null,
          filename: null,
        }
      : stepToApiPayload({
          ...draft,
          message_model_id: null,
          step_label: draft.step_label?.trim() || null,
        });

    if (!useModel) {
      const ct = payload.content_type;
      if (ct === "text" && !String(payload.text_content || "").trim()) {
        toast.error("Preencha o texto ou vincule um modelo");
        return;
      }
      if (ct === "audio" && !String(payload.media_url || "").trim()) {
        toast.error("Envie o áudio nesta etapa ou use um modelo");
        return;
      }
      if (ct === "image" && !String(payload.media_url || "").trim()) {
        toast.error("Envie a imagem nesta etapa ou use um modelo");
        return;
      }
      if (ct === "image_caption") {
        if (!String(payload.media_url || "").trim()) {
          toast.error("Envie a imagem nesta etapa ou use um modelo");
          return;
        }
        if (!String(payload.caption || "").trim()) {
          toast.error("Preencha a legenda");
          return;
        }
      }
    }

    setSaving(true);
    try {
      if (draft.id) {
        await api.put(`/recovery/steps/${draft.id}`, payload);
      } else {
        await api.post("/recovery/steps", payload);
      }
      toast.success("Etapa salva");
      setStepEditorOpen(false);
      setStepDraft(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar etapa");
    } finally {
      setSaving(false);
    }
  };

  const removeStep = async (step) => {
    if (!window.confirm("Remover esta etapa da sequência?")) return;
    try {
      await api.delete(`/recovery/steps/${step.id}`);
      toast.success("Etapa removida");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível remover");
    }
  };

  const duplicateStep = async (step) => {
    const nextOrder = Math.max(...sortedSteps.map((s) => Number(s.order || 0)), -1) + 1;
    try {
      const p = { ...stepToApiPayload(step), order: nextOrder };
      await api.post("/recovery/steps", p);
      toast.success("Etapa duplicada");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao duplicar");
    }
  };

  const moveStep = async (step, delta) => {
    const ix = sortedSteps.findIndex((s) => s.id === step.id);
    const jx = ix + delta;
    if (ix < 0 || jx < 0 || jx >= sortedSteps.length) return;
    const a = sortedSteps[ix];
    const b = sortedSteps[jx];
    setSaving(true);
    try {
      await api.put(`/recovery/steps/${a.id}`, { ...stepToApiPayload(a), order: Number(b.order) });
      await api.put(`/recovery/steps/${b.id}`, { ...stepToApiPayload(b), order: Number(a.order) });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível reordenar");
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = async (tpl) => {
    try {
      await api.post("/recovery/apply-template", { template: tpl.id });
      setSettings((prev) => ({ ...prev, selected_model: tpl.id }));
      toast.success("Modelo aplicado. Confira os lembretes abaixo.");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aplicar sugestão");
    }
  };

  const saveAndActivate = async () => {
    setSaving(true);
    try {
      await api.post("/recovery/settings", { enabled: true, selected_model: settings.selected_model });
      await api.post("/recovery/activate");
      setSettings((prev) => ({ ...prev, enabled: true }));
      toast.success("Recuperação ativada. Agora o sistema agenda lembretes para PIX não pagos.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível ativar");
    } finally {
      setSaving(false);
    }
  };

  const pauseRecovery = async () => {
    try {
      await api.post("/recovery/pause");
      setSettings((prev) => ({ ...prev, enabled: false }));
      toast.success("Recuperação pausada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível pausar");
    }
  };

  const sequenceSummary = useMemo(
    () =>
      sortedSteps.map((s) => {
        const when = timingPhrase(s);
        const type = contentTypeLabel(s.content_type);
        const modelName = s.message_model_id ? String(s.message_model_name || "").trim() || "Modelo" : "Conteúdo na etapa";
        return { id: s.id, when, type, modelName };
      }),
    [sortedSteps],
  );

  /** Resumo em uma linha para cards de modelo */
  const modelSnippet = (m) => {
    if (m.content_type === "text") return (m.text_content || "").replace(/\s+/g, " ").trim() || "Sem texto";
    if (m.content_type === "audio") return m.audio_title?.trim() || m.filename || "Áudio";
    if (m.content_type === "image_caption") return (m.caption || "").replace(/\s+/g, " ").trim() || "Imagem com legenda";
    return "Imagem";
  };

  return (
    <div className="relative mx-auto max-w-5xl space-y-12 px-4 pb-36 pt-1 sm:px-6 md:space-y-16">
      {/* Cabeçalho */}
      <header className="rounded-2xl border border-white/[0.06] bg-gradient-to-b from-slate-900/50 to-cp-elevated/40 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white md:text-[1.65rem]">Recuperação de PIX</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-300">
          Configure lembretes automáticos para recuperar PIX não pagos.
        </p>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
          PIX pendente → lembrete no WhatsApp → pagamento confirmado → sequência encerrada.
        </p>
      </header>

      {/* Situação da recuperação */}
      <section aria-labelledby="recovery-status-heading" className="rounded-2xl border border-white/[0.05] bg-white/[0.02] p-6 md:p-8">
        <h2 id="recovery-status-heading" className="font-heading text-lg font-semibold text-white">
          Situação da recuperação
        </h2>
        <p className="mt-2 max-w-xl text-sm text-slate-500">{statusHint}</p>
        <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <span
            className={`inline-flex w-fit items-center rounded-full px-3 py-1.5 text-sm ${
              settings.enabled ?
                "font-medium bg-emerald-500/12 text-emerald-300"
              : "font-semibold bg-red-500/25 text-red-100 ring-2 ring-red-500/45 shadow-[0_0_20px_-4px_rgba(239,68,68,0.45)]"
            }`}
          >
            <span className={`mr-2 h-1.5 w-1.5 rounded-full ${settings.enabled ? "bg-emerald-400" : "bg-red-300 shadow-[0_0_6px_1px_rgba(252,165,165,0.6)]"}`} />
            {settings.enabled ? "Recuperação ativada" : "Recuperação pausada"}
          </span>
          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void saveAndActivate()}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:opacity-55"
            >
              <CheckCircle2 className="h-4 w-4" />
              Ativar
            </button>
            <button
              type="button"
              onClick={() => void pauseRecovery()}
              className="inline-flex items-center gap-2 rounded-lg border border-white/[0.08] px-4 py-2 text-sm font-medium text-slate-400 transition hover:bg-white/[0.04] hover:text-slate-300"
            >
              <PauseCircle className="h-4 w-4" />
              Pausar
            </button>
          </div>
        </div>
      </section>

      {/* Sugestões prontas */}
      <section aria-labelledby="recovery-presets-heading">
        <h2 id="recovery-presets-heading" className="font-heading text-lg font-semibold text-white">
          Sugestões prontas
        </h2>
        <p className="mt-1 max-w-xl text-sm text-slate-500">Comece por um modelo e ajuste os tempos depois, se quiser.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {presets.length === 0 && (
            <p className="col-span-full rounded-xl border border-dashed border-white/[0.07] py-10 text-center text-sm text-slate-500">
              Nenhuma sugestão disponível no momento.
            </p>
          )}
          {presets.slice(0, 3).map((tpl) => (
            <article
              key={tpl.id}
              className="flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 transition hover:border-white/[0.1]"
            >
              <div className="flex items-start gap-2">
                <Wand2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500/75" aria-hidden />
                <h3 className="font-medium leading-snug text-white">{tpl.name}</h3>
              </div>
              <p className="mt-2 flex-1 text-[13px] leading-relaxed text-slate-500">{tpl.description || "Sequência sugerida pelo ConvertPix."}</p>
              <button
                type="button"
                onClick={() => void applyPreset(tpl)}
                className="mt-4 w-full rounded-lg border border-emerald-500/30 bg-emerald-500/[0.08] py-2 text-center text-xs font-semibold text-emerald-200/95 transition hover:bg-emerald-500/[0.14]"
              >
                Aplicar modelo
              </button>
            </article>
          ))}
        </div>
      </section>

      {/* Sequência + prévia WhatsApp */}
      <section className="rounded-2xl border border-white/[0.05] bg-white/[0.02] p-6 md:p-8">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(260px,36%)] lg:items-start lg:gap-10">
          <div className="min-w-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 id="recovery-steps-heading" className="font-heading text-lg font-semibold text-white">
                  Sua sequência de recuperação
                </h2>
                <p className="mt-1 max-w-xl text-sm text-slate-500">Defina quando cada lembrete será enviado e qual conteúdo será usado.</p>
              </div>
              <button
                type="button"
                onClick={openNewStep}
                className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg border border-white/[0.1] px-3 py-2 text-xs font-medium text-slate-400 transition hover:border-white/[0.14] hover:bg-white/[0.04] hover:text-slate-300 sm:self-auto sm:px-4 sm:text-sm"
              >
                <Plus className="h-4 w-4" /> Nova etapa
              </button>
            </div>

            <div className="mt-6 space-y-2.5">
              {loading ?
                <div className="rounded-xl border border-white/[0.05] py-12 text-center text-sm text-slate-500">Carregando…</div>
              : sortedSteps.length === 0 ?
                <div className="rounded-xl border border-dashed border-white/[0.07] py-12 text-center text-sm text-slate-500">
                  Nenhuma etapa ainda. Aplique uma sugestão pronta ou crie uma etapa.
                </div>
              : sortedSteps.map((step, idx) => (
                  <div
                    key={step.id}
                    className="group rounded-xl border border-white/[0.05] bg-cp-bg/25 px-3.5 py-3 transition hover:border-white/[0.08] sm:px-4 sm:py-3.5"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] text-[11px] font-semibold text-slate-500 ring-1 ring-white/[0.06]">
                          {idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] text-slate-500">
                            <span className="font-medium text-slate-400">{idx + 1}º lembrete</span>
                            <span className="mx-1.5 text-slate-600">—</span>
                            <span className="text-emerald-400/90">{timingPhrase(step)}</span>
                          </p>
                          <div className="mt-1 truncate text-sm font-medium text-white">{step.step_label?.trim() || stepOrdinalLabel(idx)}</div>
                          <p className="mt-1 text-[12px] text-slate-500">
                            {contentTypeLabel(step.content_type)}
                            <span className="mx-1.5 text-slate-600">·</span>
                            <span className="text-slate-400">
                              {step.message_model_id ? step.message_model_name || "—" : "Conteúdo na etapa"}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-0 pl-11 opacity-45 transition group-hover:opacity-100 sm:shrink-0 sm:pl-0">
                        <button
                          type="button"
                          title="Mover para cima"
                          onClick={() => void moveStep(step, -1)}
                          disabled={idx === 0 || saving}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-white/[0.06] hover:text-slate-300 disabled:opacity-25 sm:p-2"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Mover para baixo"
                          onClick={() => void moveStep(step, 1)}
                          disabled={idx === sortedSteps.length - 1 || saving}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-white/[0.06] hover:text-slate-300 disabled:opacity-25 sm:p-2"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Editar"
                          onClick={() => openStepEditor(step)}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-white/[0.06] hover:text-slate-300 sm:p-2"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Duplicar"
                          onClick={() => void duplicateStep(step)}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-white/[0.06] hover:text-slate-300 sm:p-2"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Remover"
                          onClick={() => void removeStep(step)}
                          className="rounded-md p-1.5 text-slate-500 hover:bg-red-500/10 hover:text-red-400 sm:p-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          <div className="mt-10 min-w-0 lg:mt-0 lg:sticky lg:top-4">
            <h2 className="font-heading text-base font-semibold text-white">Como o cliente vai receber</h2>
            <p className="mt-1 text-xs text-slate-500">Prévia no estilo WhatsApp (exemplos nas variáveis).</p>
            <div className="mt-4 overflow-hidden rounded-xl border border-black/10 bg-[#e5ddd5] shadow-inner">
              <div className="max-h-[min(320px,48vh)] overflow-y-auto px-3 py-3.5 sm:px-4">
                <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-wider text-slate-500/90">Cliente</p>
                <div className="space-y-3">
                  {sortedSteps.length === 0 ?
                    <p className="py-6 text-center text-xs text-slate-500">Sem etapas para mostrar.</p>
                  : sortedSteps.map((s) => {
                      const when = timingPhrase(s);
                      const ct = s.content_type || "text";
                      return (
                        <div key={s.id} className="space-y-1.5">
                          <div className="text-[10px] font-medium text-slate-600/90">{when}</div>
                          {ct === "text" && (
                            <div className="ml-auto max-w-[90%] rounded-2xl rounded-tr-sm bg-[#dcf8c6] px-3 py-2 text-[12px] leading-snug text-slate-800 shadow-sm">
                              {substituteVariables(s.text_content || "") || "…"}
                            </div>
                          )}
                          {(ct === "image" || ct === "image_caption") && (
                            <div className="ml-auto max-w-[90%] space-y-1">
                              <div className="overflow-hidden rounded-lg bg-white shadow-sm">
                                {s.media_url ?
                                  <img src={s.media_url} alt="" className="max-h-28 w-full object-cover" />
                                : <div className="flex h-16 items-center justify-center bg-white text-[11px] text-slate-500">Imagem</div>}
                              </div>
                              {ct === "image_caption" && (
                                <div className="rounded-lg rounded-tr-sm bg-[#dcf8c6] px-2.5 py-1.5 text-[12px] text-slate-800 shadow-sm">
                                  {substituteVariables(s.caption || "")}
                                </div>
                              )}
                            </div>
                          )}
                          {ct === "audio" && (
                            <div className="ml-auto w-full max-w-[min(90%,280px)]">
                              <WhatsAppAudioBubble mediaUrl={s.media_url} filename={s.filename} />
                            </div>
                          )}
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resumo dos envios */}
      <section aria-labelledby="recovery-summary-heading" className="rounded-xl border border-white/[0.04] bg-black/20 px-4 py-4 md:px-5 md:py-4">
        <div className="flex flex-wrap items-center gap-2">
          <LayoutTemplate className="h-3.5 w-3.5 text-slate-600" aria-hidden />
          <h2 id="recovery-summary-heading" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Resumo dos envios
          </h2>
        </div>
        {sequenceSummary.length === 0 ?
          <p className="mt-3 text-center text-xs text-slate-600">Configure etapas para ver o resumo.</p>
        : <ul className="mt-3 space-y-2">
            {sequenceSummary.map((row) => (
              <li key={row.id} className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-[13px]">
                <span className="font-medium text-emerald-500/85">{row.when}</span>
                <span className="text-slate-600">→</span>
                <span className="text-slate-400">{row.type}</span>
                <span className="text-slate-600">→</span>
                <span className="min-w-0 truncate text-slate-500" title={row.modelName}>
                  {row.modelName}
                </span>
              </li>
            ))}
          </ul>}
      </section>

      {/* Modelos de mensagem — secundário (recolhível) */}
      <section aria-labelledby="recovery-models-heading" className="rounded-2xl border border-white/[0.05] bg-white/[0.015]">
        <button
          type="button"
          onClick={() => setModelsSectionOpen((o) => !o)}
          aria-expanded={modelsSectionOpen}
          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition hover:bg-white/[0.02] md:px-6 md:py-4"
        >
          <div className="min-w-0">
            <span id="recovery-models-heading" className="font-heading text-sm font-semibold text-white md:text-base">
              Gerenciar modelos de mensagem
            </span>
            <p className="mt-0.5 text-[12px] text-slate-500">
              {models.length === 0 ? "Nenhum modelo salvo — expanda para criar." : `${models.length} modelo${models.length !== 1 ? "s" : ""} · opcional, para reutilizar nas etapas`}
            </p>
          </div>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-200 ${modelsSectionOpen ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
        {modelsSectionOpen ?
          <div className="border-t border-white/[0.05] px-4 pb-6 pt-4 md:px-6 md:pb-8">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={openNewModel}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 sm:w-auto"
              >
                <Plus className="h-4 w-4" /> Novo modelo
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {models.length === 0 && (
                <div className="col-span-full rounded-xl border border-dashed border-white/[0.08] py-12 text-center text-sm text-slate-500">
                  Nenhum modelo. Crie um para vincular às etapas.
                </div>
              )}
              {models.map((m) => (
                <article
                  key={m.id}
                  className="flex flex-col rounded-xl border border-white/[0.05] bg-cp-bg/30 p-4 transition hover:border-white/[0.08]"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="truncate font-medium text-white">{m.name}</h3>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white/[0.04] px-2 py-0.5 text-[11px] text-slate-400">
                      {m.content_type === "text" && <FileText className="h-3 w-3" />}
                      {(m.content_type === "image" || m.content_type === "image_caption") && <ImageIcon className="h-3 w-3" />}
                      {m.content_type === "audio" && <Mic className="h-3 w-3" />}
                      {contentTypeLabel(m.content_type)}
                    </span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-[12px] leading-snug text-slate-500">{modelSnippet(m)}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/[0.04] pt-2.5 text-[11px]">
                    <button type="button" onClick={() => openEditModel(m)} className="text-slate-500 transition hover:text-emerald-400">
                      Editar
                    </button>
                    <button type="button" onClick={() => void duplicateModel(m)} className="text-slate-500 transition hover:text-slate-300">
                      Duplicar
                    </button>
                    <button type="button" onClick={() => void deleteModel(m)} className="ml-auto text-slate-600 transition hover:text-red-400">
                      Excluir
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        : null}
      </section>

      {/* CTA fixo */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/[0.06] bg-cp-bg/94 px-4 py-3 backdrop-blur-md md:pl-[var(--sidebar-w,72px)]">
        <div className="mx-auto flex max-w-5xl flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <p className="text-center text-xs text-slate-500 sm:text-left">Revise a sequência e salve quando estiver tudo pronto.</p>
          <button
            type="button"
            onClick={() => void saveAndActivate()}
            disabled={saving}
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-sm transition hover:bg-emerald-400 disabled:opacity-55 sm:w-auto"
          >
            <MessageCircle className="h-4 w-4" />
            Salvar e ativar recuperação
          </button>
        </div>
      </div>

      {/* Modal modelo */}
      {modelEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 md:p-6">
          <div className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-cp-bg shadow-2xl">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/[0.08] px-5 py-4 md:px-6">
              <div>
                <h3 className="text-lg font-semibold text-white">{modelForm.id ? "Editar modelo" : "Novo modelo de mensagem"}</h3>
                <p className="mt-0.5 text-sm text-slate-500">Texto em sintaxe WhatsApp; prévia com variáveis de exemplo.</p>
              </div>
              <button type="button" onClick={() => setModelEditorOpen(false)} className="rounded-lg border border-white/[0.12] px-3 py-1.5 text-sm text-slate-300 hover:bg-white/[0.04]">
                Fechar
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(260px,32%)] lg:items-start">
              <div className="min-w-0 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Nome do modelo</label>
                  <input
                    value={modelForm.name}
                    onChange={(e) => setModelForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Ex.: Primeiro lembrete / Último aviso"
                    className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2 text-sm text-white placeholder:text-slate-600"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Tipo do conteúdo</label>
                  <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {CONTENT_TYPE_OPTIONS.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setModelForm((p) => ({ ...p, content_type: c.id }))}
                        className={`rounded-lg border px-2 py-2 text-center text-xs font-semibold ${
                          modelForm.content_type === c.id ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-white/[0.1] text-slate-300 hover:bg-white/[0.04]"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-600">Vídeo e documento ficarão disponíveis em breve.</p>
                </div>

                {modelForm.content_type === "text" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">Conteúdo da mensagem</label>
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
                      Use *negrito*, _itálico_, ~tachado~, `mono` e blocos {"```"}código{"```"}. Listas:{" "}
                      <code className="rounded bg-white/5 px-1 text-slate-400">* item</code> ou{" "}
                      <code className="rounded bg-white/5 px-1 text-slate-400">1. item</code>. Citação:{" "}
                      <code className="rounded bg-white/5 px-1 text-slate-400">&gt; texto</code>.
                    </p>
                    <div className="mb-2 mt-2 flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.08] bg-black/25 p-1">
                      <button type="button" title="Negrito" onClick={() => wrapModelFormat("*", "*")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                        <Bold className="h-4 w-4" />
                      </button>
                      <button type="button" title="Itálico" onClick={() => wrapModelFormat("_", "_")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                        <Italic className="h-4 w-4" />
                      </button>
                      <button type="button" title="Tachado" onClick={() => wrapModelFormat("~", "~")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                        <Strikethrough className="h-4 w-4" />
                      </button>
                      <button type="button" title="Monoespaçado" onClick={() => wrapModelFormat("`", "`")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                        <Code className="h-4 w-4" />
                      </button>
                      <span className="mx-0.5 h-5 w-px bg-white/10" aria-hidden />
                      <button
                        type="button"
                        title="Citação (início da linha)"
                        onClick={() => prefixLinesInTextarea(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "> ")}
                        className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                      >
                        <Quote className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Lista com marcador"
                        onClick={() => insertListLine(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "* ")}
                        className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                      >
                        <List className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        title="Lista numerada"
                        onClick={() => insertListLine(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "1. ")}
                        className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                      >
                        <ListOrdered className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      <span className="mr-1 self-center text-[10px] text-slate-500">Variáveis:</span>
                      {VARIABLE_BUTTONS.map((v) => (
                        <button
                          key={v.value}
                          type="button"
                          onClick={() => insertVarIntoModel(v.value, "text_content")}
                          className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200/95 transition hover:bg-emerald-500/20"
                        >
                          {v.label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      ref={modelTextareaRef}
                      rows={7}
                      value={modelTextFieldValue}
                      onChange={(e) => setModelTextFieldValue(e.target.value)}
                      spellCheck
                      className="min-h-[150px] w-full resize-y rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 font-mono text-sm leading-relaxed text-white placeholder:text-slate-600"
                      placeholder="Digite a mensagem…"
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {READY_MESSAGES.map((m) => (
                        <button
                          key={m.label}
                          type="button"
                          onClick={() => setModelTextFieldValue(m.text)}
                          className="rounded-lg border border-white/[0.08] px-2 py-1 text-[11px] text-slate-400 transition hover:bg-white/[0.04]"
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {modelForm.content_type === "image" && (
                  <div>
                    <label className="text-xs font-medium text-slate-500">Imagem</label>
                    <RecoveryFileDropzone
                      className="mt-1.5"
                      accept="image/*"
                      variant="image"
                      filename={modelForm.filename}
                      onFile={(f) => void onPickModelFile(f)}
                    />
                    <RecoveryImageUploadHint className="mt-2" />
                  </div>
                )}

                {modelForm.content_type === "image_caption" && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Imagem</label>
                      <RecoveryFileDropzone
                        className="mt-1.5"
                        accept="image/*"
                        variant="image"
                        filename={modelForm.filename}
                        onFile={(f) => void onPickModelFile(f)}
                      />
                      <RecoveryImageUploadHint className="mt-2" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Legenda</label>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-600">Mesma sintaxe do WhatsApp; prévia ao lado com exemplos nas variáveis.</p>
                      <div className="mb-2 mt-2 flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.08] bg-black/25 p-1">
                        <button type="button" title="Negrito" onClick={() => wrapModelFormat("*", "*")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                          <Bold className="h-4 w-4" />
                        </button>
                        <button type="button" title="Itálico" onClick={() => wrapModelFormat("_", "_")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                          <Italic className="h-4 w-4" />
                        </button>
                        <button type="button" title="Tachado" onClick={() => wrapModelFormat("~", "~")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                          <Strikethrough className="h-4 w-4" />
                        </button>
                        <button type="button" title="Monoespaçado" onClick={() => wrapModelFormat("`", "`")} className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white">
                          <Code className="h-4 w-4" />
                        </button>
                        <span className="mx-0.5 h-5 w-px bg-white/10" aria-hidden />
                        <button
                          type="button"
                          title="Citação (início da linha)"
                          onClick={() => prefixLinesInTextarea(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "> ")}
                          className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                        >
                          <Quote className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Lista com marcador"
                          onClick={() => insertListLine(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "* ")}
                          className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                        >
                          <List className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Lista numerada"
                          onClick={() => insertListLine(modelTextareaRef.current, modelTextFieldValue, setModelTextFieldValue, "1. ")}
                          className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                        >
                          <ListOrdered className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mb-2 flex flex-wrap gap-1.5">
                        <span className="mr-1 self-center text-[10px] text-slate-500">Variáveis:</span>
                        {VARIABLE_BUTTONS.map((v) => (
                          <button
                            key={v.value}
                            type="button"
                            onClick={() => insertVarIntoModel(v.value, "caption")}
                            className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200/95 transition hover:bg-emerald-500/20"
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                      <textarea
                        ref={modelTextareaRef}
                        rows={5}
                        value={modelTextFieldValue}
                        onChange={(e) => setModelTextFieldValue(e.target.value)}
                        spellCheck
                        className="min-h-[120px] w-full resize-y rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 font-mono text-sm leading-relaxed text-white placeholder:text-slate-600"
                        placeholder="Legenda da imagem…"
                      />
                    </div>
                  </div>
                )}

                {modelForm.content_type === "audio" && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">Arquivo de áudio</label>
                      <RecoveryFileDropzone
                        className="mt-1.5"
                        accept="audio/*"
                        variant="audio"
                        filename={modelForm.filename}
                        onFile={(f) => void onPickModelFile(f)}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">Nome para identificar (opcional)</label>
                      <input
                        value={modelForm.audio_title || ""}
                        onChange={(e) => setModelForm((p) => ({ ...p, audio_title: e.target.value }))}
                        className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2 text-sm text-white"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:sticky lg:top-0">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Prévia no WhatsApp</div>
                <p className="mt-1 text-[11px] text-slate-600">Variáveis substituídas por exemplos; formatação como no envio.</p>
                <div className="mt-3 overflow-hidden rounded-xl border border-black/10 bg-[#e5ddd5] shadow-inner">
                  <div className="max-h-[min(380px,50vh)] overflow-y-auto p-3 sm:p-4">
                    <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-wider text-slate-500/90">Cliente</p>
                    <StepContentPreview
                      contentType={modelForm.content_type}
                      textContent={modelForm.text_content}
                      caption={modelForm.caption}
                      mediaUrl={modelForm.media_url}
                      filename={modelForm.audio_title?.trim() || modelForm.filename}
                    />
                  </div>
                </div>
              </div>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-white/[0.08] px-5 py-4 md:px-6">
              <button type="button" onClick={() => setModelEditorOpen(false)} className="rounded-lg border border-white/[0.12] px-4 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/[0.04]">
                Cancelar
              </button>
              <button type="button" onClick={() => void saveModel()} disabled={saving} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:opacity-55">
                Salvar modelo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal etapa */}
      {stepEditorOpen && stepDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 md:p-6">
          <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-cp-bg shadow-2xl">
            <div className="shrink-0 border-b border-white/[0.08] px-5 py-4 md:px-6">
              <h3 className="text-lg font-semibold text-white">{stepDraft.id ? "Editar etapa" : "Nova etapa"}</h3>
              <p className="mt-0.5 text-sm text-slate-500">Lembrete automático após o PIX gerado.</p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6">
              <div
                className={
                  !stepDraft.message_model_id ?
                    "grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(260px,32%)] lg:items-start"
                  : "grid gap-6"
                }
              >
                <div className="min-w-0 space-y-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Nome da etapa (opcional)</label>
                    <input
                      value={stepDraft.step_label || ""}
                      onChange={(e) => setStepDraft((p) => ({ ...p, step_label: e.target.value || null }))}
                      placeholder={stepOrdinalLabel(sortedSteps.findIndex((s) => s.id === stepDraft.id) >= 0 ? sortedSteps.findIndex((s) => s.id === stepDraft.id) : sortedSteps.length)}
                      className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2 text-sm text-white placeholder:text-slate-600"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500">Quando enviar?</label>
                    <select
                      value={stepCustomTiming ? "__custom" : findTimingSelectValue(stepDraft)}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom") {
                          setStepCustomTiming(true);
                          return;
                        }
                        setStepCustomTiming(false);
                        const [val, unit] = v.split("_");
                        setStepDraft((p) => ({ ...p, delay_value: Number(val), delay_unit: unit }));
                      }}
                      className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 text-sm text-white"
                    >
                      {TIMING_OPTIONS.map((o) => (
                        <option key={`${o.delay_value}_${o.delay_unit}`} value={`${o.delay_value}_${o.delay_unit}`}>
                          {o.label}
                        </option>
                      ))}
                      <option value="__custom">Personalizado…</option>
                    </select>
                    {stepCustomTiming && (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="number"
                          min={1}
                          value={stepCustomNum}
                          onChange={(e) => setStepCustomNum(Number(e.target.value || 1))}
                          className="w-24 rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2 text-sm"
                        />
                        <select value={stepCustomUnit} onChange={(e) => setStepCustomUnit(e.target.value)} className="flex-1 rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2 text-sm text-white">
                          <option value="m">minutos</option>
                          <option value="h">horas</option>
                          <option value="d">dias</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {!stepDraft.message_model_id && stepDraft.id ?
                    <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2 text-xs leading-relaxed text-amber-100/90">
                      Conteúdo salvo nesta etapa (legado). Você pode vincular a um modelo abaixo; ao salvar com modelo, o envio passa a usar o modelo.
                    </div>
                  : null}

                  <div>
                    <label className="text-xs font-medium text-slate-500">Modelo de mensagem</label>
                    <select
                      value={stepDraft.message_model_id || ""}
                      onChange={(e) => setStepDraft((p) => ({ ...p, message_model_id: e.target.value || null }))}
                      className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 text-sm text-white"
                    >
                      <option value="">Personalizado</option>
                      {models.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name} ({contentTypeLabel(m.content_type)})
                        </option>
                      ))}
                    </select>
                  </div>

                  {!stepDraft.message_model_id && (
                    <>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Tipo de conteúdo</label>
                        <select
                          value={stepDraft.content_type || "text"}
                          onChange={(e) => setStepDraft((p) => ({ ...p, content_type: e.target.value }))}
                          className="mt-1.5 w-full rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 text-sm text-white"
                        >
                          {CONTENT_TYPE_OPTIONS.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(stepDraft.content_type === "text" || stepDraft.content_type === "image_caption") && (
                        <div>
                          <label className="text-xs font-medium text-slate-500">{stepDraft.content_type === "image_caption" ? "Legenda" : "Mensagem"}</label>
                          <p className="mt-1 text-[11px] leading-relaxed text-slate-600">
                            Use *negrito*, _itálico_, ~tachado~, `mono` e blocos {"```"}código{"```"}. Listas: linhas com{" "}
                            <code className="rounded bg-white/5 px-1 text-slate-400">* item</code> ou{" "}
                            <code className="rounded bg-white/5 px-1 text-slate-400">1. item</code>. Citação:{" "}
                            <code className="rounded bg-white/5 px-1 text-slate-400">&gt; texto</code>.
                          </p>
                          <div className="mb-2 mt-2 flex flex-wrap items-center gap-1 rounded-lg border border-white/[0.08] bg-black/25 p-1">
                            <button
                              type="button"
                              title="Negrito"
                              onClick={() => wrapStepFormat("*", "*")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <Bold className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Itálico"
                              onClick={() => wrapStepFormat("_", "_")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <Italic className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Tachado"
                              onClick={() => wrapStepFormat("~", "~")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <Strikethrough className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Monoespaçado"
                              onClick={() => wrapStepFormat("`", "`")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <Code className="h-4 w-4" />
                            </button>
                            <span className="mx-0.5 h-5 w-px bg-white/10" aria-hidden />
                            <button
                              type="button"
                              title="Citação (início da linha)"
                              onClick={() => prefixLinesInTextarea(stepTextareaRef.current, stepTextFieldValue, setStepTextFieldValue, "> ")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <Quote className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Lista com marcador"
                              onClick={() => insertListLine(stepTextareaRef.current, stepTextFieldValue, setStepTextFieldValue, "* ")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <List className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              title="Lista numerada"
                              onClick={() => insertListLine(stepTextareaRef.current, stepTextFieldValue, setStepTextFieldValue, "1. ")}
                              className="rounded-md p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                            >
                              <ListOrdered className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            {VARIABLE_BUTTONS.map((v) => (
                              <button
                                key={v.value}
                                type="button"
                                onClick={() =>
                                  setStepDraft((p) =>
                                    p.content_type === "image_caption" ?
                                      { ...p, caption: `${p.caption || ""}${v.value}` }
                                    : { ...p, text_content: `${p.text_content || ""}${v.value}` },
                                  )
                                }
                                className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-200/95 transition hover:bg-emerald-500/20"
                              >
                                {v.label}
                              </button>
                            ))}
                          </div>
                          <textarea
                            ref={stepTextareaRef}
                            rows={6}
                            value={stepTextFieldValue}
                            onChange={(e) => setStepTextFieldValue(e.target.value)}
                            spellCheck
                            className="min-h-[140px] w-full resize-y rounded-lg border border-white/[0.1] bg-cp-elevated px-3 py-2.5 font-mono text-sm leading-relaxed text-white placeholder:text-slate-600"
                            placeholder="Digite a mensagem…"
                          />
                        </div>
                      )}
                      {stepDraft.content_type !== "text" && (
                        <div>
                          <label className="text-xs font-medium text-slate-500">Arquivo</label>
                          <RecoveryFileDropzone
                            className="mt-1.5"
                            accept={stepDraft.content_type === "audio" ? "audio/*" : "image/*"}
                            variant={stepDraft.content_type === "audio" ? "audio" : "image"}
                            filename={stepDraft.filename}
                            onFile={(f) => {
                              const fr = new FileReader();
                              fr.onload = () =>
                                setStepDraft((p) => ({
                                  ...p,
                                  media_url: String(fr.result || ""),
                                  mimetype: f.type,
                                  filename: f.name,
                                }));
                              fr.readAsDataURL(f);
                            }}
                          />
                          {(stepDraft.content_type === "image" || stepDraft.content_type === "image_caption") && (
                            <RecoveryImageUploadHint className="mt-2" />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {!stepDraft.message_model_id ?
                  <div className="lg:sticky lg:top-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Prévia no WhatsApp</div>
                    <p className="mt-1 text-[11px] text-slate-600">Variáveis com dados de exemplo. O envio usa o mesmo texto que você edita.</p>
                    <div className="mt-3 overflow-hidden rounded-xl border border-black/10 bg-[#e5ddd5] shadow-inner">
                      <div className="max-h-[min(340px,45vh)] overflow-y-auto p-3 sm:p-4">
                        <p className="mb-2 text-center text-[10px] font-medium uppercase tracking-wider text-slate-500/90">Cliente</p>
                        <StepContentPreview
                          contentType={stepDraft.content_type}
                          textContent={stepDraft.text_content}
                          caption={stepDraft.caption}
                          mediaUrl={stepDraft.media_url}
                          filename={stepDraft.filename}
                        />
                      </div>
                    </div>
                  </div>
                : null}
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2 border-t border-white/[0.08] bg-cp-bg/95 px-5 py-4 md:px-6">
              <button type="button" onClick={() => setStepEditorOpen(false)} className="rounded-lg border border-white/[0.12] px-4 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/[0.04]">
                Cancelar
              </button>
              <button type="button" onClick={() => void saveStep()} disabled={saving} className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:opacity-55">
                Salvar etapa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
